import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  Check,
  ArrowRight,
  ExternalLink,
  Star,
  Loader2,
  ShieldCheck,
  RefreshCw,
  PenTool,
  ChevronDown,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { API_URL } from "../utils/api";
import { captureAttribution, getAttribution } from "../utils/attribution";
import { getConsentLeadFields } from "../utils/cookieConsent";
import { trackGenerateLead } from "../utils/leadTracking";

const GOOGLE_REVIEWS_URL = "https://share.google/9RTbLJTbmmTSv7A83";
const HERO_IMAGE = "/img/hemsidor/case-nordenstad-1280.webp";
const LEGACY_REDIRECT_URLS = new Set(["/hemsida-foretag", "/ny-hemsida-foretag"]);

function useSmoothScroll() {
  return (hash) => {
    const id = (hash || "").replace("#", "");
    if (!id) return false;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      return true;
    }
    return false;
  };
}

export default function ElidoHomePage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: "", company: "", email: "", message: "", website_url: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formLoadedAt] = useState(Date.now());
  const [openFaq, setOpenFaq] = useState(-1);
  const scrollTo = useSmoothScroll();

  useEffect(() => {
    captureAttribution();
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/website/content`);
        const data = await res.json();
        setContent(data.content);
      } catch (e) {
        console.error("Failed to load /hemsidor content:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading && window.location.hash.length > 1) {
      setTimeout(() => scrollTo(window.location.hash), 150);
    }
  }, [loading]); // eslint-disable-line

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (contactForm.website_url) {
      toast.success("Tack för ditt meddelande! Vi återkommer snart.");
      setContactForm({ name: "", company: "", email: "", message: "", website_url: "" });
      return;
    }
    if (Date.now() - formLoadedAt < 2000) {
      toast.error("Vänligen ta dig tid att fylla i formuläret ordentligt");
      return;
    }
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Fyll i alla obligatoriska fält");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: contactForm.name,
          company: contactForm.company,
          email: contactForm.email,
          message: contactForm.message,
          attribution: getAttribution(),
          ...getConsentLeadFields(),
          _form_loaded_at: formLoadedAt,
        }),
      });
      if (response.status === 429) {
        const d = await response.json();
        toast.error(d.message || "Du har gjort för många förfrågningar. Vänta en stund.");
        return;
      }
      if (response.ok) {
        let result = null;
        try { result = await response.json(); } catch { /* ignore parse errors */ }
        trackGenerateLead({
          accepted: result?.accepted,
          lead_id: result?.lead_id,
          form_source: "hemsidor_contact",
        });
        toast.success("Tack för ditt meddelande! Vi återkommer snart.");
        setContactForm({ name: "", company: "", email: "", message: "", website_url: "" });
      } else {
        throw new Error("Failed");
      }
    } catch (err) {
      toast.error("Kunde inte skicka meddelandet. Försök igen.");
    } finally {
      setSubmitting(false);
    }
  };

  const ctaClick = (e, link) => {
    if (link && link.startsWith("#")) {
      if (scrollTo(link)) e.preventDefault();
    }
  };

  if (loading) {
    return (
      <div className="elido-delayed-public-loader min-h-[70vh] flex items-center justify-center bg-white" data-testid="hemsidor-loading">
        <Loader2 className="w-7 h-7 text-[#255E69] animate-spin" />
      </div>
    );
  }

  const c = content || {};
  const visible = (key) => c[key] && c[key].visible !== false;
  const hero = c.hero || {};
  const cases = c.cases || {};
  const nor = c.new_or_rebuild || {};
  const why = c.why_elido || {};
  const pricing = c.pricing_hemsidor || {};
  const process = c.process || {};
  const personal = c.personal || {};
  const faq = c.faq || {};
  const seo = c.seo_links || {};
  const cta = c.cta || {};
  const contact = c.contact || {};

  const norIcons = [RefreshCw, PenTool];
  const faqItems = [...(faq.items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="font-['Inter'] bg-white text-[#0b1f27]" data-testid="hemsidor-page">
      <Helmet>
        <title>Professionella hemsidor för företag | Elido</title>
        <meta name="description" content="Elido bygger professionella hemsidor för företag med tydlig struktur, mobilanpassning och fokus på fler förfrågningar. Från 2 990 kr exkl. moms." />
      </Helmet>
      <Toaster position="top-center" richColors />

      {/* ── HERO ─────────────────────────────────────────────── */}
      {visible("hero") && (
        <section className="relative overflow-hidden bg-gradient-to-b from-[#FBF7F2] to-white" data-testid="hemsidor-hero">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 pb-12 lg:pt-20 lg:pb-16 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-5 px-3 py-1.5 rounded-full bg-white/70 border border-[#255E69]/15 backdrop-blur" data-testid="hero-eyebrow">
                {hero.eyebrow || "Hemsidor för företag"}
              </div>
              <h1 className="font-['Barlow_Condensed'] text-[42px] leading-[1.02] sm:text-6xl lg:text-[80px] lg:leading-[0.98] font-bold tracking-[-0.02em] text-balance" data-testid="hero-title">
                {hero.headline || "Moderna hemsidor för företag som vill framåt"}
              </h1>
              <p className="mt-5 text-[16px] sm:text-[17px] lg:text-[18px] text-[#334155] leading-relaxed max-w-xl">
                {hero.subheadline}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {hero.cta_primary?.text && (
                  <a
                    href={hero.cta_primary.link || "#kontakt"}
                    onClick={(e) => ctaClick(e, hero.cta_primary.link)}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#255E69] text-white text-[15px] font-semibold hover:bg-[#1e4d56] transition-colors"
                    data-testid="hero-cta-primary"
                  >
                    {hero.cta_primary.text}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                {hero.cta_secondary?.text && (
                  <a
                    href={hero.cta_secondary.link || "#kundcase"}
                    onClick={(e) => ctaClick(e, hero.cta_secondary.link)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-[#255E69]/25 text-[#255E69] bg-white text-[15px] font-semibold hover:bg-[#255E69]/5 transition-colors"
                    data-testid="hero-cta-secondary"
                  >
                    {hero.cta_secondary.text}
                  </a>
                )}
              </div>
              <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2.5 text-[13.5px] text-[#334155] max-w-md">
                {["Fast pris", "Ni äger hemsidan", "Direktkontakt med oss som bygger", "Ingen bindningstid"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#3A8A98] mt-[3px] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_40px_80px_-30px_rgba(15,42,48,0.4)]">
                <img
                  src={hero.hero_image || HERO_IMAGE}
                  alt={hero.hero_image_alt || "Företagshemsida byggd av Elido"}
                  width="1280"
                  height="960"
                  className="w-full h-auto"
                  fetchpriority="high"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>

          {/* Trust bar */}
          <div className="border-t border-slate-200/70 bg-white/60 backdrop-blur">
            <div className="max-w-6xl mx-auto px-4 sm:px-5 lg:px-8 py-4">
              <div className="flex flex-col items-center gap-y-2.5 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-7 sm:gap-y-2.5">
                <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group" data-testid="google-reviews-link">
                  <span className="flex items-center gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (<Star key={i} className="w-4 h-4 fill-[#F5A65B] text-[#F5A65B]" />))}
                  </span>
                  <span className="text-[13.5px] sm:text-[14px] font-semibold group-hover:text-[#255E69] transition-colors whitespace-nowrap">5/5 på Google</span>
                  <ExternalLink className="w-3.5 h-3.5 text-[#94a3b8] group-hover:text-[#255E69]" />
                </a>
                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:contents">
                  {["Fast pris", "Ni äger hemsidan", "Direktkontakt med oss som bygger"].map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[13px] sm:text-[13.5px] text-[#334155]">
                      <Check className="w-4 h-4 text-[#3A8A98] shrink-0" />
                      <span className="whitespace-nowrap">{t}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── KUNDCASE ─────────────────────────────────────────── */}
      {visible("cases") && (
        <section id="kundcase" className="bg-white py-14 lg:py-24 scroll-mt-24" data-testid="hemsidor-cases">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">{cases.eyebrow}</div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.12]">{cases.title}</h2>
              <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">{cases.subtitle}</p>
            </div>
            <div className="mt-9 grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {(cases.items || []).map((item, i) => (
                <article key={i} className="group flex flex-col rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_18px_40px_-28px_rgba(15,42,48,0.35)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_28px_60px_-28px_rgba(15,42,48,0.4)]" data-testid={`case-${i}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    {item.image_url ? (
                      <img src={item.image_url} alt={`Hemsidan ${item.name} — byggd av Elido`} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">Ingen bild</div>
                    )}
                  </div>
                  <div className="flex flex-col flex-1 p-5 lg:p-6">
                    <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#255E69]">{item.category}</div>
                    <h3 className="mt-2 text-[17px] lg:text-[18px] font-bold leading-snug">{item.name}</h3>
                    <p className="mt-2 text-[14px] text-[#334155] leading-relaxed flex-1">{item.description}</p>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#255E69] hover:text-[#0e2f36] transition-colors" data-testid={`case-${i}-link`}>
                        <span>Besök hemsidan</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── NY ELLER BYGGA OM ────────────────────────────────── */}
      {visible("new_or_rebuild") && (
        <section className="bg-[#FBF7F2] py-14 lg:py-20" data-testid="hemsidor-new-or-rebuild">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">{nor.eyebrow}</div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.12]">{nor.title}</h2>
              <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">{nor.intro}</p>
            </div>
            <div className="mt-8 grid gap-4 sm:gap-5 sm:grid-cols-2">
              {(nor.options || []).map((opt, i) => {
                const Icon = norIcons[i % norIcons.length];
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-7" data-testid={`new-or-rebuild-${i}`}>
                    <div className="w-11 h-11 rounded-xl bg-[#255E69]/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#255E69]" />
                    </div>
                    <h3 className="text-[18px] lg:text-xl font-bold leading-snug">{opt.title}</h3>
                    <p className="mt-2 text-[14.5px] text-[#334155] leading-relaxed">{opt.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── VARFÖR ELIDO ─────────────────────────────────────── */}
      {visible("why_elido") && (
        <section className="bg-white py-14 lg:py-24" data-testid="hemsidor-why">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-4 px-3 py-1.5 rounded-full bg-[#255E69]/5 border border-[#255E69]/15">
                <ShieldCheck className="w-3 h-3" /> Därför Elido
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.12]">{why.title}</h2>
              <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">{why.subtitle}</p>
            </div>
            <div className="lg:col-span-7">
              <ul className="rounded-2xl border border-slate-200 bg-[#FBF7F2] divide-y divide-slate-200/70 overflow-hidden">
                {(why.items || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3.5 px-5 py-4 lg:px-6" data-testid={`why-item-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-[#3A8A98]/12 border border-[#3A8A98]/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#255E69]" />
                    </span>
                    <div>
                      <div className="text-[15px] font-semibold leading-snug">{item.title}</div>
                      {item.description && <div className="mt-0.5 text-[14px] text-[#334155] leading-relaxed">{item.description}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* ── PRISER ───────────────────────────────────────────── */}
      {visible("pricing_hemsidor") && (
        <section id="priser" className="bg-[#0F2A30] py-16 lg:py-24 scroll-mt-24" data-testid="hemsidor-pricing">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5DBCCB] mb-3">Priser &amp; vad som ingår</div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.12] text-white">{pricing.title}</h2>
              <p className="mt-4 text-[15px] sm:text-[16px] text-white/70 leading-relaxed">{pricing.subtitle}</p>
            </div>
            <div className="mt-10 grid gap-5 lg:gap-6 md:grid-cols-3">
              {(pricing.tiers || []).map((tier, i) => (
                <div
                  key={i}
                  className={`flex flex-col rounded-2xl p-6 lg:p-7 border ${tier.highlighted ? "bg-white border-white shadow-[0_30px_70px_-25px_rgba(0,0,0,0.6)]" : "bg-white/5 border-white/12"}`}
                  data-testid={`pricing-tier-${i}`}
                >
                  <h3 className={`text-[17px] font-bold ${tier.highlighted ? "text-[#0b1f27]" : "text-white"}`}>{tier.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className={`text-[13px] ${tier.highlighted ? "text-[#64748b]" : "text-white/55"}`}>från</span>
                    <span className={`text-3xl font-bold ${tier.highlighted ? "text-[#0b1f27]" : "text-white"}`}>{tier.price_from}</span>
                    <span className={`text-[14px] ${tier.highlighted ? "text-[#64748b]" : "text-white/55"}`}>{tier.period}</span>
                  </div>
                  <p className={`mt-3 text-[14px] leading-relaxed ${tier.highlighted ? "text-[#334155]" : "text-white/70"}`}>{tier.description}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {(tier.features || []).map((f, fi) => (
                      <li key={fi} className={`flex items-start gap-2.5 text-[13.5px] ${tier.highlighted ? "text-[#1e293b]" : "text-white/80"}`}>
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.highlighted ? "text-[#255E69]" : "text-[#5DBCCB]"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {tier.cta_text && (
                    <a
                      href={tier.cta_link || "#kontakt"}
                      onClick={(e) => ctaClick(e, tier.cta_link)}
                      className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[14px] font-semibold transition-colors ${tier.highlighted ? "bg-[#255E69] text-white hover:bg-[#1e4d56]" : "border border-white/25 text-white hover:bg-white/10"}`}
                      data-testid={`pricing-cta-${i}`}
                    >
                      {tier.cta_text}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
            {pricing.footnote && <p className="mt-6 text-[13px] text-white/50 max-w-3xl">{pricing.footnote}</p>}
          </div>
        </section>
      )}

      {/* ── PROCESS ──────────────────────────────────────────── */}
      {visible("process") && (
        <section className="bg-gradient-to-b from-[#FBF7F2] to-white py-14 lg:py-20" data-testid="hemsidor-process">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl mb-9">
              <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">Processen</div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.12]">{process.title}</h2>
              {process.subtitle && <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">{process.subtitle}</p>}
            </div>
            <div className="grid gap-4 lg:gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {(process.steps || []).map((s, i) => (
                <div key={i} className="relative bg-white rounded-2xl border border-slate-200 p-5 lg:p-6" data-testid={`process-step-${i}`}>
                  <div className="flex items-baseline gap-2.5">
                    <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#94a3b8]">Steg</span>
                    <span className="text-4xl lg:text-[2.75rem] font-bold text-[#F5A65B] leading-none tracking-tight">{s.number}</span>
                  </div>
                  <h3 className="mt-4 text-[17px] lg:text-lg font-bold leading-snug">{s.title}</h3>
                  <p className="mt-2 text-[14px] text-[#334155] leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── PERSONLIG KONTAKT ────────────────────────────────── */}
      {visible("personal") && (
        <section className="bg-[#0F2A30] py-14 lg:py-20" data-testid="hemsidor-personal">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5DBCCB] mb-3">{personal.eyebrow}</div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.2rem] font-bold tracking-tight leading-[1.12] text-white">{personal.title}</h2>
              <p className="mt-4 text-[15px] sm:text-[16px] text-white/75 leading-relaxed">{personal.description}</p>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-lg" data-testid="personal-people">
              {(personal.people || []).map((p, i) => (
                <div key={i} data-testid={`person-${i}`}>
                  <div className="flex items-center gap-3">
                    {p.image_url && <img src={p.image_url} alt={`${p.name}, ${p.role}`} width="160" height="160" loading="lazy" className="w-14 h-14 rounded-full object-cover border border-white/15 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[14.5px] font-semibold text-white leading-tight">{p.name}</div>
                      <div className="text-[13px] text-white/70 leading-tight mt-0.5">{p.role}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-col gap-1 text-[14px]">
                    {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="text-[#5DBCCB] hover:text-white transition-colors">{p.phone}</a>}
                    {p.email && <a href={`mailto:${p.email}`} className="text-[#5DBCCB] hover:text-white transition-colors break-all">{p.email}</a>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ──────────────────────────────────────────────── */}
      {visible("faq") && (
        <section className="bg-white py-14 lg:py-20" data-testid="hemsidor-faq">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.12]">{faq.title}</h2>
              {faq.subtitle && <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">{faq.subtitle}</p>}
            </div>
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              {faqItems.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} data-testid={`faq-item-${i}`}>
                    <button
                      onClick={() => setOpenFaq(isOpen ? -1 : i)}
                      className="w-full flex items-center justify-between gap-4 py-5 text-left"
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="text-[16px] font-semibold pr-2">{item.question}</span>
                      <ChevronDown className={`w-5 h-5 text-[#255E69] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && <p className="pb-5 -mt-1 text-[15px] text-[#334155] leading-relaxed" data-testid={`faq-answer-${i}`}>{item.answer}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── SEO-LÄNKAR ───────────────────────────────────────── */}
      {visible("seo_links") && (
        <section className="bg-[#FBF7F2] py-14 lg:py-18" data-testid="hemsidor-seo-links">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{seo.title}</h2>
              {seo.subtitle && <p className="mt-3 text-[15px] text-[#334155] leading-relaxed">{seo.subtitle}</p>}
            </div>
            <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(seo.links || []).filter((l) => !LEGACY_REDIRECT_URLS.has(l.url)).map((l, i) => (
                <a key={i} href={l.url} className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#255E69]/40 hover:shadow-sm transition-all" data-testid={`seo-link-${i}`}>
                  <ArrowRight className="w-4 h-4 text-[#255E69] mt-1 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                  <span>
                    <span className="block text-[14.5px] font-semibold group-hover:text-[#255E69] transition-colors">{l.title}</span>
                    {l.description && <span className="block mt-0.5 text-[13px] text-[#64748b] leading-snug">{l.description}</span>}
                  </span>
                </a>
              ))}
            </div>
            {seo.description && <p className="mt-8 text-[13.5px] text-[#64748b] leading-relaxed max-w-4xl">{seo.description}</p>}
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────────── */}
      {visible("cta") && (
        <section className="bg-white py-14 lg:py-20" data-testid="hemsidor-cta">
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <div className="rounded-3xl bg-gradient-to-br from-[#255E69] to-[#3A8A98] p-8 lg:p-12 text-center text-white">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">{cta.title}</h2>
              {cta.subtitle && <p className="mt-4 text-[15px] sm:text-[16px] text-white/85 leading-relaxed max-w-2xl mx-auto">{cta.subtitle}</p>}
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                {cta.cta_primary?.text && (
                  <a href={cta.cta_primary.link || "#kontakt"} onClick={(e) => ctaClick(e, cta.cta_primary.link)} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#255E69] text-[15px] font-semibold hover:bg-white/90 transition-colors" data-testid="cta-primary">
                    {cta.cta_primary.text}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                {cta.cta_secondary?.text && (
                  <a href={cta.cta_secondary.link || "#kontakt"} onClick={(e) => ctaClick(e, cta.cta_secondary.link)} className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-white/40 text-white text-[15px] font-semibold hover:bg-white/10 transition-colors" data-testid="cta-secondary">
                    {cta.cta_secondary.text}
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── KONTAKT ──────────────────────────────────────────── */}
      {visible("contact") && (
        <section id="kontakt" className="bg-[#FBF7F2] py-14 lg:py-24 scroll-mt-24" data-testid="hemsidor-contact">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <div className="mb-8 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">{contact.title}</h2>
              {contact.subtitle && <p className="mt-3 text-[15px] sm:text-[16px] text-[#334155]">{contact.subtitle}</p>}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-[14px]">
                {contact.email && <a href={`mailto:${contact.email}`} className="text-[#255E69] font-semibold hover:underline">{contact.email}</a>}
                {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="text-[#255E69] font-semibold hover:underline">{contact.phone}</a>}
              </div>
            </div>
            <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 space-y-4" data-testid="contact-form">
              <input type="text" tabIndex={-1} autoComplete="off" value={contactForm.website_url} onChange={(e) => setContactForm({ ...contactForm, website_url: e.target.value })} className="hidden" aria-hidden="true" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#475569] mb-1.5">Namn *</label>
                  <input type="text" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30" data-testid="contact-name" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#475569] mb-1.5">Företag</label>
                  <input type="text" value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30" data-testid="contact-company" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#475569] mb-1.5">E-post *</label>
                <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30" data-testid="contact-email" />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#475569] mb-1.5">Vad behöver ni hjälp med? *</label>
                <textarea required rows={4} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/30" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#255E69] text-white py-3.5 rounded-full font-semibold hover:bg-[#1e4d56] transition-colors disabled:opacity-50" data-testid="contact-submit">
                {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Skickar…</>) : (<><span className="whitespace-nowrap">Få ett förslag</span><ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>
        </section>
      )}
    </div>
  );
}
